/**
 * Per-request access re-validation.
 *
 * The add-in loads Studio settings once on sign-in, but an admin can change a
 * user in AiVA Studio mid-session — deactivate/archive/delete them, change their
 * persona, or change their channel/module/connection access. To make those
 * changes reflect immediately, every backend request is preceded by
 * `ensureAccess()`, which re-fetches the settings endpoint and compares it to the
 * snapshot captured at load. On a material change it records a reason, notifies
 * the app shell (which blocks the surface with a notice), and throws
 * `AccessRevokedError` so the pending request never runs against stale access.
 *
 * This module is intentionally a leaf: it imports no other studio module at
 * runtime (only a type-only import of StudioSettingsData). `AppConfigProvider`
 * registers the actual `validate`/`onBlocked` behaviour via `registerAccessGuard`
 * so `fetchWithRefresh`/`serviceFetch` can call `ensureAccess()` without pulling
 * in the whole settings/auth stack (avoids an import cycle).
 */
import type { AccessBlockReason, StudioSettingsData } from "./types";

export class AccessRevokedError extends Error {
  constructor(public readonly reason: AccessBlockReason) {
    super(`Access re-validation failed: ${reason}`);
    this.name = "AccessRevokedError";
  }
}

/** Stable projection of the settings fields that gate the add-in surface. */
export interface AccessSnapshot {
  userStatus: string;
  hasChannelAccess: boolean;
  /** Why hasChannelAccess is false ("channel_disabled" | "persona_no_channel"), else null. */
  accessDeniedReason: string | null;
  personaId: number | null;
  /** Order-independent signature of modules + their connectors. */
  modulesSig: string;
  /** Order-independent signature of connections + their status. */
  connectionsSig: string;
}

/**
 * Runs one settings re-validation. Returns the reason the surface should be
 * blocked, or null when access is unchanged. Must not throw for transient
 * failures (network/5xx) — those fail open so a backend hiccup can't lock a
 * legitimately-entitled user out of every request.
 */
export type ValidateAccessFn = () => Promise<AccessBlockReason | null>;

interface AccessGuardState {
  validate: ValidateAccessFn;
  onBlocked: (reason: AccessBlockReason) => void;
  blockedReason: AccessBlockReason | null;
  inFlight: Promise<void> | null;
}

let state: AccessGuardState | null = null;
let revalidateInFlight: Promise<AccessBlockReason | null> | null = null;

/**
 * Wire up the guard. Called by AppConfigProvider once the settings baseline is
 * established. Registering also clears any previously-recorded block so a fresh
 * baseline (e.g. after a reload) starts clean.
 */
export function registerAccessGuard(opts: {
  validate: ValidateAccessFn;
  onBlocked: (reason: AccessBlockReason) => void;
}): void {
  state = {
    validate: opts.validate,
    onBlocked: opts.onBlocked,
    blockedReason: null,
    inFlight: null,
  };
}

/** Tear down the guard (sign-out / unmount / tests). */
export function resetAccessGuard(): void {
  state = null;
  revalidateInFlight = null;
}

/**
 * Re-validate access on demand and return the reason the surface should change
 * (or null when access is unchanged, or the guard isn't registered).
 *
 * Unlike {@link ensureAccess}, this neither records a global block nor throws —
 * the caller decides what to do with the reason. The Knowledge Base uses it to
 * re-check access alongside its direct-to-Graph calls (which bypass
 * `fetchWithRefresh`, so they never hit `ensureAccess`): a module-level change
 * (persona/module-routing removed the module) redirects to the modules grid,
 * while an account/channel revocation blocks the whole surface. Concurrent
 * callers coalesce onto a single in-flight settings fetch.
 */
export async function revalidateAccess(): Promise<AccessBlockReason | null> {
  const s = state;
  if (!s) return null;
  if (revalidateInFlight) return revalidateInFlight;

  const run = s.validate().catch(() => null);
  revalidateInFlight = run;
  // Clear the in-flight slot when settled so the next check re-validates.
  void run.finally(() => {
    if (revalidateInFlight === run) revalidateInFlight = null;
  });
  return run;
}

/**
 * Clear a recorded block without dropping the registration. Called after a
 * successful settings (re)load so a fresh baseline starts unblocked; the next
 * request re-validates from scratch.
 */
export function clearAccessBlock(): void {
  if (state) state.blockedReason = null;
}

/**
 * Validate access before a backend request. No-op when unregistered (pre-login /
 * standalone / tests). Once a block has been recorded it throws immediately for
 * every subsequent call without re-fetching. Concurrent callers coalesce onto a
 * single in-flight settings fetch.
 */
export async function ensureAccess(): Promise<void> {
  const s = state;
  if (!s) return;
  if (s.blockedReason) throw new AccessRevokedError(s.blockedReason);
  if (s.inFlight) return s.inFlight;

  const run = (async () => {
    const reason = await s.validate();
    if (reason) {
      s.blockedReason = reason;
      s.onBlocked(reason);
      throw new AccessRevokedError(reason);
    }
  })();

  // Clear the in-flight slot when settled so the next request re-validates.
  s.inFlight = run.finally(() => {
    if (state === s) s.inFlight = null;
  });
  return s.inFlight;
}

/** Project settings into the fields that gate the surface. */
export function computeSnapshot(data: StudioSettingsData): AccessSnapshot {
  const byText = (a: string, b: string) => a.localeCompare(b);
  const modulesSig = data.modules
    .map((m) => `${m.id}:${m.connectors.map((c) => c.systemName).sort(byText).join("|")}`)
    .sort(byText)
    .join(",");
  const connectionsSig = data.connections
    .map((c) => `${c.addinKey}:${c.status}`)
    .sort(byText)
    .join(",");
  return {
    userStatus: (data.user.status || "active").toLowerCase(),
    hasChannelAccess: data.hasChannelAccess,
    accessDeniedReason: data.accessDeniedReason ?? null,
    personaId: data.user.persona?.id ?? null,
    modulesSig,
    connectionsSig,
  };
}

/** Map the wire access-denied reason to the specific channel block reason. */
function channelReason(accessDeniedReason: string | null): AccessBlockReason {
  return accessDeniedReason === "channel_disabled" ? "channel_disabled" : "persona_no_channel";
}

/**
 * Map a non-active user status (from the domain UserStatus enum, lowercased) to
 * its block reason so the add-in can show the exact "account is inactive /
 * suspended / archived …" message. Unknown non-active values fall back to
 * "inactive" (the generic soft-deleted state).
 */
export function statusToReason(status: string): AccessBlockReason {
  switch (status.toLowerCase()) {
    case "suspended":
      return "suspended";
    case "archived":
      return "archived";
    case "invited":
      return "invited";
    case "purged":
      return "purged";
    case "inactive":
    default:
      return "inactive";
  }
}

/**
 * Compare a fresh snapshot against the baseline captured at load. Order of
 * checks reflects severity: account revoked → lost channel access → catalogue
 * changed.
 */
export function compareSnapshots(
  base: AccessSnapshot,
  next: AccessSnapshot,
): AccessBlockReason | null {
  if (next.userStatus !== "active") return statusToReason(next.userStatus);
  if (!next.hasChannelAccess) return channelReason(next.accessDeniedReason);
  if (next.personaId !== base.personaId) return "persona_changed";
  if (next.modulesSig !== base.modulesSig) return "catalogue_changed";
  if (next.connectionsSig !== base.connectionsSig) return "catalogue_changed";
  return null;
}
